import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaWallet, FaChartLine, FaFileInvoice } from 'react-icons/fa';
import './Finance.css';

const Finance = () => {
  const { user } = useAuth();
  const [financeData, setFinanceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.userType === 'seller') {
      fetchFinanceData();
    }
  }, [user]);

  const fetchFinanceData = async () => {
    try {
      const response = await api.get('/finance/dashboard');
      setFinanceData(response.data.finance);
    } catch (error) {
      console.error('Failed to fetch finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLoan = async () => {
    const amount = prompt('Enter loan amount (₹):');
    if (!amount) return;

    const purpose = prompt('Purpose of loan:');
    if (!purpose) return;

    try {
      const response = await api.post('/finance/apply-loan', {
        amount: Number(amount),
        purpose
      });
      toast.success('Loan application submitted successfully!');
      fetchFinanceData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to apply for loan');
    }
  };

  const handleGenerateInvoice = async () => {
    toast.info('Invoice generation feature - Connect to order system');
  };

  if (user?.userType !== 'seller') {
    return (
      <Layout>
        <div>
          <h1>Finance Dashboard</h1>
          <p>This feature is only available for sellers.</p>
        </div>
      </Layout>
    );
  }

  if (loading) return <Layout><div>Loading...</div></Layout>;

  return (
    <Layout>
      <div className="finance-page">
        <h1>Finance & Working Capital</h1>
        <p>Manage your business finances and access instant credit</p>

        <div className="finance-grid">
          <div className="card finance-card">
            <div className="card-header">
              <FaChartLine className="card-icon" />
              <h2>Credit Health Score</h2>
            </div>
            <div className="score-display">
              <div className="score-number">{financeData?.creditHealthScore || 0}</div>
              <div className="score-max">/ 1000</div>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(financeData?.creditHealthScore || 0) / 10}%` }}
              />
            </div>
          </div>

          <div className="card finance-card">
            <div className="card-header">
              <FaWallet className="card-icon" />
              <h2>Loan Eligibility</h2>
            </div>
            <div className="loan-details">
              <div className="loan-row">
                <span>Current Limit:</span>
                <strong>₹{financeData?.loanEligibility?.currentLimit?.toLocaleString('en-IN') || 0}</strong>
              </div>
              <div className="loan-row">
                <span>Available Limit:</span>
                <strong>₹{financeData?.loanEligibility?.availableLimit?.toLocaleString('en-IN') || 0}</strong>
              </div>
              <div className="loan-row">
                <span>Next Tier:</span>
                <strong>₹{financeData?.loanEligibility?.nextTierLimit?.toLocaleString('en-IN') || 0}</strong>
              </div>
            </div>
            <button 
              className="btn btn-primary" 
              style={{ width: '100%', marginTop: '16px' }}
              onClick={handleApplyLoan}
            >
              Apply for Instant Credit
            </button>
          </div>

          <div className="card finance-card">
            <div className="card-header">
              <FaChartLine className="card-icon" />
              <h2>Sales Velocity</h2>
            </div>
            <div className="sales-stats">
              <div className="stat-item">
                <span>Monthly:</span>
                <strong>₹{financeData?.salesVelocity?.monthly?.toLocaleString('en-IN') || 0}</strong>
              </div>
              <div className="stat-item">
                <span>Quarterly:</span>
                <strong>₹{financeData?.salesVelocity?.quarterly?.toLocaleString('en-IN') || 0}</strong>
              </div>
              <div className="stat-item">
                <span>Yearly:</span>
                <strong>₹{financeData?.salesVelocity?.yearly?.toLocaleString('en-IN') || 0}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header">
            <FaFileInvoice className="card-icon" />
            <h2>Recent Invoices</h2>
          </div>
          {financeData?.invoices && financeData.invoices.length > 0 ? (
            <div className="invoices-list">
              {financeData.invoices.map((invoice, index) => (
                <div key={index} className="invoice-item">
                  <div>
                    <strong>{invoice.invoiceNumber}</strong>
                    <p>{new Date(invoice.generatedAt).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div>₹{invoice.amount?.toLocaleString('en-IN')}</div>
                  <button className="btn btn-outline">Download</button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '20px' }}>
              <p>No invoices generated yet</p>
              <button className="btn btn-primary" onClick={handleGenerateInvoice} style={{ marginTop: '12px' }}>
                Generate Invoice
              </button>
            </div>
          )}
        </div>

        {financeData?.loanApplications && financeData.loanApplications.length > 0 && (
          <div className="card" style={{ marginTop: '24px' }}>
            <div className="card-header">
              <FaWallet className="card-icon" />
              <h2>Loan Applications</h2>
            </div>
            <div className="loans-list">
              {financeData.loanApplications.map((loan, index) => (
                <div key={index} className="loan-item">
                  <div>
                    <strong>Application ID: {loan.applicationId}</strong>
                    <p>{loan.purpose}</p>
                    <small>{new Date(loan.appliedAt).toLocaleDateString('en-IN')}</small>
                  </div>
                  <div>
                    <div className="loan-amount">₹{loan.amount?.toLocaleString('en-IN')}</div>
                    <span className={`badge ${
                      loan.status === 'approved' ? 'badge-success' :
                      loan.status === 'rejected' ? 'badge-danger' :
                      'badge-warning'
                    }`}>
                      {loan.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Finance;
